import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { FormStatus } from '@wix/form-viewer';
import { FormViewer } from '@wix/form-viewer/widget';
import type { FormViewerHandle, FormValues, FormError } from '@wix/form-viewer/widget';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { Text } from '@wix/wix-ui-tpa/cssVars';
import { useTranslation, useBi, usePanorama } from '@wix/yoshi-flow-editor';
import { useLogger } from '../../../../../hooks/useLogger';
import { CheckoutStage } from '../../../../../types/common';
import { reportClientAuditLog } from '../../../../../utils/bi';
import { CheckoutStyledButton } from '../CheckoutStyledButton';
import { FormPreview } from '../FormPreview';
import { StepSection, Action } from '../StepSection';
import { StepAPI, StepProps } from '../types';
import { classes } from './AdditionalInfoStep.st.css';

interface AdditionalInfoStepProps extends StepProps {
  plan: PublicPlan;
  onChange: (formValues: FormValues) => void;
  onIsFormBusyChange: (isBusy: boolean) => void;
  validateSubmission: (formValues: FormValues) => Promise<void>;
  biCheckoutStage: (stage: CheckoutStage) => void;
  isFormBusy: boolean;
  guestCheckoutEnabled: boolean;
}

export const AdditionalInfoStep: React.FC<AdditionalInfoStepProps> = ({
  position,
  onApiSet,
  onCompleted,
  setActive,
  isActiveStep,
  isLastStep,
  isNarrow,
  state,
  plan,
  onChange,
  validateSubmission,
  biCheckoutStage,
  onIsFormBusyChange,
  isFormBusy,
  guestCheckoutEnabled,
}) => {
  const logger = useLogger();

  logger.log('AdditionalInfoStep()', {
    position,
    isActiveStep,
    isLastStep,
    isNarrow,
    state,
    plan,
    guestCheckoutEnabled,
  });
  const { t, i18n } = useTranslation();
  const panorama = usePanorama();
  const biLogger = useBi();

  const formViewer = useRef<FormViewerHandle>(null);
  const isFormValid = useRef<boolean>(false);
  const [isFormValidating, setIsFormValidating] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [formErrors, setFormErrors] = useState<FormError[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const timeoutId = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    biCheckoutStage(CheckoutStage.CUSTOM_CHECKOUT_LOADED);
  }, []);

  const handleFormChange = useCallback(
    (values: FormValues) => {
      logger.log('handleFormChange', values);
      setFormValues(values);
      isFormValid.current = false;
      onChange(values);
    },
    [onChange],
  );

  const handleFormStatusChange = useCallback(
    (status: FormStatus) => {
      const isBusy = status === 'BUSY';

      if (isBusy) {
        timeoutId.current = setTimeout(() => {
          panorama.errorMonitor().reportError(new Error('Form status change to "Ready" took longer than 20 seconds'));
          onIsFormBusyChange(false);
          reportClientAuditLog(biLogger, {
            entityType: 'Form',
            message: 'handleFormStatusChange - timeout',
            entityId: plan.formId!,
          });
        }, 20000);
      } else {
        clearTimeout(timeoutId.current);
      }

      onIsFormBusyChange(isBusy);
    },
    [onIsFormBusyChange, panorama, biLogger, plan.formId],
  );

  const handleFormValidate = useCallback((errors: FormError[]) => {
    logger.log('handleFormValidate', errors);
    setFormErrors(errors);
  }, []);

  const handleError = useCallback(
    (error: Error) => {
      logger.log('handleError', error);
      biCheckoutStage(CheckoutStage.CUSTOM_CHECKOUT_FORM_VIEWER_FAILED_TO_LOAD);
      reportClientAuditLog(biLogger, {
        entityType: 'AdditionalInfoStep',
        message: 'handleError - form viewer failed to load',
        entityId: plan.formId!,
        error,
      });
      setHasError(true);
    },
    [logger, biCheckoutStage, biLogger, plan.formId],
  );

  const handleClick = useCallback(async () => {
    logger.log('handleClick');
    biCheckoutStage(CheckoutStage.CUSTOM_CHECKOUT_CONTINUE);

    if (!formViewer.current) {
      logger.log('handleClick - no formViewer');
      return;
    }

    setIsFormValidating(true);
    logger.log('handleClick - setIsFormValidating');
    const isValid = await formViewer.current.validate();
    logger.log('handleClick - isValid', isValid);
    isFormValid.current = isValid;
    setIsFormValidating(false);

    if (!isValid) {
      return;
    }

    validateSubmission(formValues);

    logger.log('handleClick - onCompleted');
    onCompleted();
  }, [logger, biCheckoutStage, onCompleted, validateSubmission, formValues]);

  const api: StepAPI = useMemo(
    () => ({
      execute: () => handleClick(),
      isValid: () => isFormValid.current,
    }),
    [handleClick, isFormValid],
  );

  useEffect(() => {
    onApiSet(api);
  }, [api]);

  return (
    <StepSection
      dataHook={VIEWER_HOOKS.ADDITIONAL_INFO_STEP_SECTION}
      position={position}
      title={t('payment.checkout-steps.additional-info.title')}
      state={state}
      action={
        state === 'completed' ? (
          <Action onClick={setActive}>{t('payment.checkout-steps.additional-info.edit-button')}</Action>
        ) : undefined
      }
    >
      {state === 'active' ? (
        hasError ? (
          <div className={classes.root}>
            <div className={classes.emptyState}>
              <div className={classes.emptyStateContent}>
                <Text className={classes.emptyStateTitle} tagName="h3">
                  {t('payment.checkout-steps.additional-info.empty-state-title')}
                </Text>
                <Text className={classes.emptyStateText} tagName="p">
                  {t('payment.checkout-steps.additional-info.empty-state-text')}
                </Text>
              </div>
            </div>
            {!isLastStep || isNarrow ? (
              <CheckoutStyledButton
                className={classes.button}
                onClick={onCompleted}
                label={t('payment.checkout-steps.additional-info.skip-button')}
              />
            ) : null}
          </div>
        ) : (
          <div className={classes.root}>
            <FormViewer
              i18n={i18n}
              bi={biLogger}
              panorama={panorama}
              ref={formViewer}
              formId={plan.formId!}
              values={formValues}
              errors={formErrors}
              onStatusChange={handleFormStatusChange}
              onChange={handleFormChange}
              onValidate={handleFormValidate}
              onError={handleError}
            />
            {!isLastStep || isNarrow ? (
              <CheckoutStyledButton
                dataHook={VIEWER_HOOKS.ADDITIONAL_INFO_STEP_CONTINUE_BUTTON}
                className={classes.button}
                onClick={handleClick}
                disabled={isFormValidating || isFormBusy}
                loading={isFormBusy}
                label={t('payment.checkout-steps.additional-info.continue-button')}
              />
            ) : null}
          </div>
        )
      ) : state === 'completed' && !isEmpty(formValues) ? (
        <div className={classes.root}>
          <FormPreview formId={plan.formId!} formValues={formValues} />
        </div>
      ) : null}
    </StepSection>
  );
};

export default AdditionalInfoStep;
